import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'redux-first-history';

import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { SimpleNewsletter } from '@dev/ee-api/ee-api-models';

import { PaidNewslettersPaths, pathsParams } from '@/services/dictionaries/Paths';
import { DeletePaidNewsletter } from '@/store/paidNewsletters/actions';
import { DropdownOptions } from '@/store/paidNewsletters/types';
import { ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';

import { PAID_NEWSLETTER_DELETE_CONFIRMATION_TEXT } from '@/components/PaidNewsletters/PaidNewslettersInterface';
import { usePaidNewsletterParamsFromUrl } from '@/components/PaidNewsletters/hooks/useRequestParams';
import { PaidNewsletterLinkModalBody } from '@/components/PaidNewsletters/PaidNewslettersList/components/PaidNewsletterLinkModalBody';
import { PaidNewsletterDeleteModalBody } from '@/components/PaidNewsletters/PaidNewslettersList/components/PaidNewsletterDeleteModalBody';
import { SetCampaignCreatorOpen } from '@/store/campaigns/actions';
import { useContactListsNamesMap } from '@/components/LandingPageCreator/hooks/useContactLists';
import { getConfigStore } from '@/store/config/actions';
import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { ContactsPaths } from '@/services/dictionaries/Paths';


export const useDropdownActions = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const urlParams = usePaidNewsletterParamsFromUrl();
    const contactListsMap = useContactListsNamesMap();
    const config = useSelector(getConfigStore);


    const handleDeletePaidNewsletter = (name: string) => {
        const changeDeleteConfirmationText = (text): void => {
            setDeleteConfirmationText(text);
            dispatch(UpdateDialog({ isDisabled: text !== PAID_NEWSLETTER_DELETE_CONFIRMATION_TEXT }));
        };

        const dialogParams: ShowDialogParams = {
            title: t('deletepaidnewsletter', { count: 1 }),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeletePaidNewsletter([name], urlParams));
                setDeleteConfirmationText('');
            },
            cancelLabel: t('cancel'),
            onClickCancel: () => setDeleteConfirmationText(''),
            body: <PaidNewsletterDeleteModalBody
                checkedPaidNewsletters={[name]}
                deleteConfirmationText={deleteConfirmationText}
                setDeleteConfirmationText={setDeleteConfirmationText}
                changeDeleteConfirmationText={changeDeleteConfirmationText}
            />,
            isDisabled: deleteConfirmationText !== PAID_NEWSLETTER_DELETE_CONFIRMATION_TEXT
        };

        dispatch(ShowDialog(dialogParams));
    };

    const handleLinkPaidNewsletter = (paidNewsletter: SimpleNewsletter) => {
        const dialogParams: ShowDialogParams = {
            title: t('paidnewsletterlink'),
            body: <PaidNewsletterLinkModalBody paidNewsletter={paidNewsletter}/>,
            width: '820px',
            cancelLabel: t('close')
        };

        dispatch(ShowDialog(dialogParams));
    };
    const handleSendCampaign = (paidNewsletter: SimpleNewsletter) => {
        const listId = contactListsMap.get(paidNewsletter.Audience[0]);

        if (config.isStandalone) {
            dispatch(SetCampaignCreatorOpen(true, listId));
            return;
        }
        const url = parseLegacyUrl(
            ContactsPaths.CampaignCreatorTargetOld.replace(':listname', safelyEncodeBase64Url(listId)),
            config.isStandalone
        );
        dispatch(push(url));
    }

    const commonOptions = {
        [DropdownOptions.PaidNewsletterLink]: (paidNewsletter: SimpleNewsletter) => handleLinkPaidNewsletter(paidNewsletter),
        [DropdownOptions.EditPaidNewsletter]: (paidNewsletter: SimpleNewsletter) =>                             
            {dispatch(push(PaidNewslettersPaths.PaidNewsletterCreator.replace(pathsParams.nameOptional, safelyEncodeBase64Url(paidNewsletter.Name))))},
        [DropdownOptions.SendCampaign]: (paidNewsletter: SimpleNewsletter) => handleSendCampaign(paidNewsletter),
        [DropdownOptions.Delete]: (paidNewsletter: SimpleNewsletter) => handleDeletePaidNewsletter(paidNewsletter.Name),
    };

    const getDropdownActions = (
        option: { label?: string; value: DropdownOptions }, 
        paidNewsletter: SimpleNewsletter
    ): void => commonOptions[option.value](paidNewsletter);

    return { 
        getDropdownActions,
        showPaidNewsletterLinkModal: handleLinkPaidNewsletter
    }
};
